import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import Logout from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import Divider from '@mui/material/Divider'
import MuiDrawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import { createTheme, styled, ThemeProvider } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { DefaultTheme } from '@mui/system'
import * as React from 'react'
import { organizationAdminItems, productAdminItems } from '../components/listItems'
import Loader from '../components/Loader'
import Toasts from '../components/Toasts'
import Stack, { StackContext, Upload } from '../contexts/stackContext'
import UserContext, { UserContextModel } from '../contexts/userContext'
import { useFetchLoggedUser, logout } from '../hooks/api'
import goToLoginPage from '../utils/goToLoginPage'

const drawerWidth = 240
const ERROR_DURABILITY_MS = 10000

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9),
      },
    }),
  },
}))

const mdTheme = createTheme()

interface ThemeProviderProps<Theme = DefaultTheme> {
  children?: React.ReactNode
  theme: Partial<Theme> | ((outerTheme: Theme) => Theme)
}

type Props = {
  children: JSX.Element
  uri: string
}

export default function Layout({ children, uri }: Props): React.ReactElement<ThemeProviderProps<DefaultTheme>> {
  const [loadingUser, user, userError] = useFetchLoggedUser()
  const [stack, setStack] = React.useState<JSX.Element[]>([])
  const [uploads, setUploads] = React.useState<Upload[]>([])
  const [open, setOpen] = React.useState(true)

  const toggleDrawer = () => {
    setOpen(!open)
  }

  const title =
    {
      '/': 'Portal',
      '/organizations': 'Organizations',
      '/organizationAdmins': 'Organization administrators',
      '/patients': 'Patients',
    }[uri] || 'Portal'

  if (loadingUser) {
    return <Loader />
  }

  if (userError?.status === 401) {
    // Redirect is done in the fetch hook.
    return <Loader />
  } else if (userError || !user) {
    // TODO: Error handling
    return <div>Error</div>
  }

  const userContext = new UserContextModel(user)
  const stackContext = new StackContext(stack, uploads, setStack, setUploads)

  return (
    <ThemeProvider theme={mdTheme}>
      <UserContext.Provider value={userContext}>
        <Stack.Provider value={stackContext}>
          <Box sx={{ display: 'flex' }}>
            <CssBaseline />

            <AppBar position="absolute" open={open}>
              <Toolbar
                sx={{
                  pr: '24px', // keep right padding when drawer closed
                }}
              >
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="open drawer"
                  onClick={toggleDrawer}
                  sx={{
                    marginRight: '36px',
                    ...(open && { display: 'none' }),
                  }}
                >
                  <MenuIcon />
                </IconButton>

                <Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
                  {title}
                </Typography>

                <IconButton
                  color="inherit"
                  onClick={async () => {
                    try {
                      await logout()
                    } finally {
                      goToLoginPage()
                    }
                  }}
                >
                  <Logout />
                </IconButton>
              </Toolbar>
            </AppBar>

            <Drawer variant="permanent" open={open}>
              <Toolbar
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  px: [1],
                }}
              >
                <IconButton onClick={toggleDrawer}>
                  <ChevronLeftIcon />
                </IconButton>
              </Toolbar>

              <Divider />

              {userContext.isProductAdmin() && (
                <>
                  <List>{productAdminItems}</List>
                  <Divider />
                </>
              )}

              {userContext.isOrganizationAdmin() && (
                <>
                  <List>{organizationAdminItems}</List>
                  <Divider />
                </>
              )}
            </Drawer>

            <Box
              component="main"
              sx={{
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
                flexGrow: 1,
                height: '100vh',
                overflow: 'auto',
                padding: '2em',
              }}
            >
              <Toolbar />

              {children}
            </Box>

            <Toasts />
          </Box>
        </Stack.Provider>
      </UserContext.Provider>
    </ThemeProvider>
  )
}
